import { useRouter } from 'next/router'
import Link from 'next/link'
import { useState, useEffect } from 'react'
import LandingSubText from '../components/ui/landing-sub-text'
import DynamicIcon from '../components/ui/dynamic-icon'
import LandingPageHeader from '../components/ui/landing-page-header'
import LandingHeroHeading from '../components/ui/landing-hero-heading'
import LandingPageButton from '../components/ui/landing-page-button'
import LandingHeadingText from '../components/ui/landing-heading-text'
import LandingCardsGrid from '../components/ui/landing-cards-grid'
import Image from 'next/image'
import LandingMotionTexts from '../components/ui/landing-motion-texts'
import LandingPageContainer from '../components/layout/landing-page-container'
import { refreshToken, tokenExpiry, isTokenExpired, getTokenExpiry, withActiveToken } from '../lib/auth'
import Loading from '../components/ui/loading'
import { getAPIURL, getCachedBusiness, setCachedBusiness } from '../lib/utils'
import { useTranslations } from 'next-intl'

const Landing = () => {
    const router = useRouter()
    const [tokenExpiryDate, setTokenExpiryDate] = useState(null);
    const t = useTranslations('landing');
    const cachedBusiness = getCachedBusiness();


    const fetchBusinessList = () => {
        
        return withActiveToken(() => {
            fetch(getAPIURL("businesses/"), {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
            }).then((res) => {
                if (res.ok) {
                    return res.json().then((json) => {
                        const activeBusiness = json[0];
                        router.push(`/${activeBusiness.uuid}/home`);
                        setCachedBusiness(activeBusiness);

                        return;
                    });
                }
                return res.json().then((json) => Promise.reject(json));
            });
        }, router);
    }

    useEffect(() => {
        const handleAuth = async () => {
            try {
                await refreshToken();
                setTokenExpiryDate(getTokenExpiry());

                if (!isTokenExpired(getTokenExpiry()) && cachedBusiness) {
                    router.push(`/${cachedBusiness}/home`);
                } else if (!isTokenExpired(getTokenExpiry()) && !cachedBusiness) {
                    await fetchBusinessList();
                }
            } catch (error) {
                setTokenExpiryDate(new Date(Date.now() - 60000)); // Set to 1 minute from now
            }
        };

        handleAuth();

    }, [router, cachedBusiness]);

    const [showBlog, setShowBlog] = useState(true)

    if (tokenExpiryDate === null) return <Loading />
    if (isTokenExpired(tokenExpiryDate)) {
        return (
            <div className='h-screen w-screen overflow-y-auto custom-scrollbar relative bg-white'>
                <div className='w-full bg-[#0e0e0f] bg-[url("/images/landing-hero-bg.png")] bg-cover bg-no-repeat bg-center'>
                    <LandingPageHeader />
                    <div className='grid gap-[30px] lg:gap-[40px] items-center justify-center pb-[300px] lg:pb-[316px] pt-[200px] max-w-[80%] lg:max-w-[750px] m-auto'>
                        <div className='grid gap-[12px] lg:gap-[28px] justify-center'>
                            <div className='grid gap-[8px]'>
                                <p className='hidden lg:block text-center font-semibold text-white tracking-[0.288px]'>{t('workspaceEvolved')}</p>
                                <LandingHeroHeading>{t('heroTitle')}</LandingHeroHeading>
                            </div>
                            <p className=' text-center !text-[14px] lg:!text-[18px] !leading-[21px] lg:!leading-[28px] text-[#FFFFFF99] !max-w-[562px] m-auto'>{t('heroSubtitle')}</p>
                        </div>
                        <LandingPageButton onClick={() => router.push('/login')}>{t('seeInAction')}</LandingPageButton>
                    </div>
                </div>
                <LandingPageContainer>
                    <div className='mt-[100px] lg:mt-[139px] mb-[100px] lg:mb-[157px] grid gap-[20px]'>
                        <div className='flex items-center lg:justify-between'>
                            <LandingHeadingText additionalclasses='!text-start'>{t('everythingYouNeed')}<br />{t('nothingYouDont')}</LandingHeadingText>
                            <LandingSubText additionalclasses='hidden lg:block !max-w-[300px] !text-start'>{t('fullControl')}</LandingSubText>
                        </div>
                        <LandingCardsGrid />
                    </div>
                    <div className='grid gap-[50px] mb-[98px] lg:mb-[209px] max-w-[1261px] m-auto'>
                        <div className='max-w-[90%] lg:max-w-[750px] m-auto grid gap-[8px]'>
                            <LandingHeadingText additionalclasses='!text-[25px] lg:!text-[30px]'>{t('useItYourWay')}</LandingHeadingText>
                            <LandingSubText>{t('useItYourWayDesc')}</LandingSubText>
                        </div>
                        <Image src='/images/use-your-way.png' alt='landing' width={1216} height={906} className='m-auto w-full max-w-[95%] lg:max-w-[1261px] rounded-[20px]' />
                    </div>
                </LandingPageContainer>
                <LandingMotionTexts />
                <LandingPageContainer>
                    <div className='grid content-center bg-[url(/images/start-transforming-bg.png)] bg-center bg-cover bg-grey-900 py-[27px] lg:py-[172px] w-full rounded-[17px] lg:rounded-[55px] mt-[100px] lg:mt-[75px] mb-[78px] lg:mb-[40px] gap-[30px]'>
                        <LandingHeadingText additionalclasses='!text-white !text-[18px] lg:!text-[40px] !leading-[20px] lg:!leading-[44px] max-w-[206px] lg:!max-w-[664px] m-auto'>{t('startTransforming')}</LandingHeadingText>
                        <LandingPageButton onClick={() => router.push('/register')} additionalclasses='!px-[20px]'>{t('getStarted')}</LandingPageButton>
                    </div>
                </LandingPageContainer>
                <div className='bg-grey-900 bg-[url("/images/landing-footer.png")] bg-no-repeat bg-cover bg-bottom w-full pt-[130px] lg:pt-[329px] pb-[234px] lg:pb-[540px]'>
                    <div className='grid gap-[8px] max-w-[80%] lg:max-w-[792px] m-auto '>
                        <p className='font-light text-[16px] text-white tracking-[1.09px] text-center'>{t('workpegLabel')}</p>
                        <p className='text-[30px] lg:text-[40px] font-medium text-white text-center m-auto'>{t('footerTitle')}<br />{t('footerSubtitle')}</p>
                    </div>
                </div>
            </div>
        )
    }
    return null
}
Landing.isUnprotected = true;
export default Landing